import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useThemeId } from './ThemeContext';

/**
 * Токены 1:1 с макетом Figma (фрейм «мои боты», десктоп SMS).
 * Тёмные токены повторяют ту же геометрию, но в SMS-like dark palette.
 */
export const SMS_PAGE = {
  bg: '#fcfcfc',
  card: '#f0f0f0',
  row: '#e8eaec',
  text: '#0d0d0d',
  muted: '#56606c',
  accent: '#FFB300',
  accentHover: '#e6a200',
  greenBot: '#31774f',
  errorRed: '#ff0004',
  triggerChipBg: '#c8c8c8',
  triggerChipText: '#000000',
  rowBorder: 'rgba(0,0,0,0.2)',
  shadow: '0px 1px 1px 0px rgba(0,0,0,0.14)',
  borderInput: 'rgba(13,13,13,0.23)',
  borderInputHover: 'rgba(13,13,13,0.35)',
  disabledBtnBg: 'rgba(13,13,13,0.12)',
  disabledBtnText: 'rgba(13,13,13,0.3)',
  outlineBorder: 'rgba(0,0,0,0.12)',
  divider: 'rgba(0,0,0,0.08)',
  white: '#ffffff',
  backButtonBg: 'rgba(255, 179, 0, 0.16)',
  backButtonBgHover: 'rgba(255, 179, 0, 0.28)',
  /** Заголовок H1 в макете */
  titlePx: { xs: '1.35rem', sm: '1.75rem', md: '28px' },
  titleWeight: 600,
  /** Подзаголовок под H1 */
  subtitleSize: '1rem',
  /** «Токен вашего бота» */
  sectionTitleSize: 16,
  sectionTitleWeight: 500,
  /** Подсказка под секцией */
  hintSize: 14,
  hintWeight: 500,
} as const;

export const SMS_DARK_PAGE = {
  bg: '#171717',
  card: '#252525',
  row: '#303030',
  text: '#f5f5f5',
  muted: '#b8c0ca',
  accent: SMS_PAGE.accent,
  accentHover: SMS_PAGE.accentHover,
  greenBot: '#4f9f72',
  errorRed: '#ff5f63',
  triggerChipBg: '#3b3b3b',
  triggerChipText: '#f5f5f5',
  rowBorder: 'rgba(255,255,255,0.14)',
  shadow: '0px 1px 1px 0px rgba(0,0,0,0.55)',
  borderInput: 'rgba(255,255,255,0.23)',
  borderInputHover: 'rgba(255,255,255,0.38)',
  disabledBtnBg: 'rgba(255,255,255,0.12)',
  disabledBtnText: 'rgba(255,255,255,0.34)',
  outlineBorder: 'rgba(255,255,255,0.12)',
  divider: 'rgba(255,255,255,0.1)',
  white: '#252525',
  backButtonBg: 'rgba(255, 179, 0, 0.16)',
  backButtonBgHover: 'rgba(255, 179, 0, 0.28)',
} as const;

export function smsPageTokens(isSmsLight: boolean) {
  return isSmsLight ? SMS_PAGE : SMS_DARK_PAGE;
}

/** Иконка корзины в конструкторе сцен (Figma: 16×18 px, #FF0004) */
export const flowDeleteIconSx: SxProps<Theme> = {
  width: 16,
  height: 18,
  fontSize: 16,
  color: SMS_PAGE.errorRed,
  flexShrink: 0,
};

export function useSmsPageDesign() {
  const theme = useTheme();
  const { themeId } = useThemeId();
  const isSmsLight = themeId === 'smsby' && theme.palette.mode === 'light';
  return { theme, themeId, isSmsLight };
}

export function smsPaperCardSx(_theme: Theme, isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    bgcolor: tokens.card,
    border: isSmsLight ? 'none' : `1px solid ${tokens.outlineBorder}`,
    boxShadow: tokens.shadow,
    borderRadius: '8px',
  };
}

export function smsGridCardSx(_theme: Theme, isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    bgcolor: tokens.card,
    border: isSmsLight ? 'none' : `1px solid ${tokens.outlineBorder}`,
    boxShadow: tokens.shadow,
    borderRadius: '8px',
    '&:hover': {
      boxShadow: isSmsLight ? '0px 2px 8px rgba(0,0,0,0.1)' : '0px 2px 10px rgba(0,0,0,0.65)',
      borderColor: isSmsLight ? undefined : tokens.accent,
    },
  };
}

export function smsSearchFieldSx(isSmsLight: boolean): SxProps<Theme> | undefined {
  const tokens = smsPageTokens(isSmsLight);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      bgcolor: tokens.white,
      '& fieldset': { borderColor: tokens.borderInput },
      '&:hover fieldset': { borderColor: tokens.borderInputHover },
    },
  };
}

export function smsTokenFieldSx(isSmsLight: boolean): SxProps<Theme> | undefined {
  const tokens = smsPageTokens(isSmsLight);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      minHeight: 40,
      bgcolor: tokens.white,
      '& fieldset': { borderColor: tokens.borderInput },
      '&:hover fieldset': { borderColor: tokens.borderInputHover },
    },
  };
}

export function smsConnectButtonSx(isSmsLight: boolean, disabled: boolean, loading?: boolean): SxProps<Theme> {
  const base: SxProps<Theme> = {
    textTransform: 'uppercase',
    borderRadius: '100px',
  };
  if (!isSmsLight) return base;
  if (loading) {
    return {
      ...base,
      bgcolor: '#B0BEC5',
      color: '#ffffff',
      boxShadow: 'none',
      '&:hover': { bgcolor: '#9EA7AD' },
      '&.Mui-disabled': {
        bgcolor: '#B0BEC5',
        color: 'rgba(255,255,255,0.7)',
      },
    };
  }
  if (!disabled) return base;
  return {
    ...base,
    bgcolor: SMS_PAGE.disabledBtnBg,
    color: SMS_PAGE.disabledBtnText,
    boxShadow: 'none',
    '&:hover': { bgcolor: 'rgba(13,13,13,0.16)' },
    '&.Mui-disabled': {
      bgcolor: SMS_PAGE.disabledBtnBg,
      color: SMS_PAGE.disabledBtnText,
    },
  };
}

export function smsChipBotUsernameSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.greenBot,
    color: SMS_PAGE.white,
    fontWeight: SMS_PAGE.sectionTitleWeight,
    height: 37,
    borderRadius: '32px',
    '& .MuiChip-label': { px: 2.5 },
  };
}

export function smsChipWebhookActiveSx(): SxProps<Theme> {
  return {
    height: 37,
    borderRadius: '32px',
    borderColor: SMS_PAGE.greenBot,
    color: SMS_PAGE.greenBot,
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}

export function smsChipBotStoppedSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.white,
    color: SMS_PAGE.errorRed,
    borderColor: SMS_PAGE.errorRed,
    height: 37,
    borderRadius: '32px',
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}

export function smsStartBotButtonSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.accent,
    color: SMS_PAGE.white,
    border: `1px solid ${SMS_PAGE.accent}`,
    '&:hover': { bgcolor: SMS_PAGE.accentHover, borderColor: SMS_PAGE.accentHover },
  };
}

export function smsAssignScenarioButtonSx(): SxProps<Theme> {
  return {
    color: SMS_PAGE.accent,
    borderColor: SMS_PAGE.accent,
    '&:hover': {
      borderColor: SMS_PAGE.accentHover,
      bgcolor: 'rgba(255, 179, 0, 0.08)',
    },
  };
}

export function smsTriggerChipSx(theme: Theme, isDup: boolean): SxProps<Theme> {
  if (isDup) {
    return {
      bgcolor: 'rgba(255,255,255,0.9)',
      color: theme.palette.error.main,
      fontWeight: SMS_PAGE.sectionTitleWeight,
      height: 30,
      borderRadius: '81px',
      minWidth: 65,
      border: `1px solid ${theme.palette.error.main}`,
    };
  }
  return {
    bgcolor: SMS_PAGE.triggerChipBg,
    color: SMS_PAGE.triggerChipText,
    fontWeight: SMS_PAGE.sectionTitleWeight,
    height: 30,
    borderRadius: '81px',
    minWidth: 65,
  };
}

export function smsScenarioRowSx(theme: Theme, isSmsLight: boolean, isDup: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    bgcolor: tokens.row,
    border: `1px solid ${isDup ? theme.palette.error.main : tokens.rowBorder}`,
  };
}

export function smsEditorToolbarSx(isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 2,
    py: 1.25,
    minHeight: 56,
    bgcolor: tokens.bg,
    borderBottom: `1px solid ${tokens.divider}`,
  };
}

export const smsBackIconButtonProps = {
  sx: {
    width: 42,
    height: 42,
    p: '10px',
    bgcolor: SMS_PAGE.backButtonBg,
    color: 'primary.main',
    '&:hover': { bgcolor: SMS_PAGE.backButtonBgHover },
  },
} as const;

/** Тулбар над холстом: перенос строк, без горизонтального «убегания» за край (от ~1440px и ниже). */
export function smsFlowToolbarPanelSx(isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  const base = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    alignContent: 'flex-start',
    gap: 1,
    px: { xs: 1, sm: 1.5, md: 2 },
    py: 1,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    rowGap: 1,
  } as const;
  return {
    ...base,
    borderRadius: '8px',
    bgcolor: tokens.white,
    border: `1px solid ${tokens.outlineBorder}`,
    boxShadow: tokens.shadow,
  };
}

export function smsDialogPaperSx(isSmsLight: boolean, extra?: SxProps<Theme>): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  const base: SxProps<Theme> = {
    bgcolor: tokens.white,
    backgroundImage: 'none',
    borderRadius: '8px',
    border: `1px solid ${tokens.outlineBorder}`,
    boxShadow: tokens.shadow,
  };
  return extra ? ([base, extra] as SxProps<Theme>) : base;
}

export function smsFlowCanvasChromeSx(isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    borderRadius: 1,
    overflow: 'hidden',
    border: `1px solid ${isSmsLight ? 'rgba(0,0,0,0.1)' : tokens.outlineBorder}`,
    flex: 1,
  };
}

export function smsFlowLeftColumnSx(isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    borderRight: `1px solid ${tokens.divider}`,
    bgcolor: tokens.card,
  };
}

export function smsFloatingSaveBarSx(isSmsLight: boolean): SxProps<Theme> {
  const tokens = smsPageTokens(isSmsLight);
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    px: 2,
    py: 1,
    borderRadius: '12px',
    bgcolor: tokens.white,
    border: `1px solid ${tokens.outlineBorder}`,
    boxShadow: tokens.shadow,
  };
}

/** Чип триггера в сетке цепочек (карточки) */
export function smsGridTriggerChipSx(): SxProps<Theme> {
  return {
    bgcolor: SMS_PAGE.triggerChipBg,
    color: SMS_PAGE.triggerChipText,
    fontWeight: SMS_PAGE.sectionTitleWeight,
  };
}
